// Libs
import classNames from 'classnames/bind';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FormProvider, UseFormReturn, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { CustomerAdditionalContact, CustomerAddress } from '~/components/module/customer';
import {
  BaseInput,
  BaseInputPhone,
  BaseSelect,
  FileUpload,
  Loading,
  UnderDevelopment,
} from '~/components/common';
// Others
import { IFormCustomer, IPayloadEditCustomer } from '~/utils/interface/customer';
import { IBaseOption, IFileUpload, IQueryParamBase } from '~/utils/interface/common';
import {
  BACK_TO_PREVIOUS_PAGE,
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILE_UPLOAD_DEFAULT_ACCEPT_IMAGE,
  typeLinkQuickBooksOptions,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { createCustomer, editCustomer, getCustomerDetail } from '~/thunks/customer/customerThunk';
import {
  createFormData,
  formatPhoneNumber,
  isEmptyObject,
  removeEmptyObjects,
  removeNonDigits,
  splitFullName,
} from '~/utils/helper/common';
import { defaultValuesAdditionalContact, defaultValuesAddress, defaultValuesForm, schema } from './helper';
import useDebounce from '~/utils/hooks/useDebounce';
import { mockDataCustomerTags } from '~/utils/mockData/customer';
import { TypeLinkQuickBooksEnum } from '~/utils/enum/common';
import { getListCustomerQuickBooks } from '~/thunks/quickBooks/quickBooksThunk';
import { ICustomerQuickBooks } from '~/utils/interface/quickBooks';
// Styles, images, icons
import styles from './FormCustomer.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const FormCustomer = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/customer', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: customerId } = useParams();
  const isEditMode = useMemo(() => !!customerId, [customerId]);

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<IFormCustomer>({
    resolver: yupResolver(schema(t, isEditMode)),
    defaultValues: defaultValuesForm,
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'customerContacts' });

  const {
    fields: fieldsAddress,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({ control, name: 'addresses' });

  const addresses = watch('addresses') || [];
  const typeLinkQuickBooks = watch('typeLinkQuickBook');
  const quickBooksCustomerId = watch('quickBookCustomerId');
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCustomerQB, setIsLoadingCustomerQB] = useState<boolean>(false);
  const [customerQBOptions, setCustomerQBOptions] = useState<(IBaseOption & ICustomerQuickBooks)[]>([]);
  const [customerQBSearchKey, setCustomerQBSearchKey] = useState<string>(EMPTY_STRING);
  const debounceCustomerQBSearchKey = useDebounce(customerQBSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (customerId) return;

    if (!quickBooksCustomerId) {
      reset({ ...defaultValuesForm, typeLinkQuickBook: typeLinkQuickBooks });
    }

    if (typeLinkQuickBooks !== TypeLinkQuickBooksEnum.ADD_EXISTING) return;

    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceCustomerQBSearchKey && { textSearch: debounceCustomerQBSearchKey }),
    };

    handleGetListCustomerQuickBooks(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceCustomerQBSearchKey, typeLinkQuickBooks]);

  useEffect(() => {
    if (!customerId) return;
    handleGetDetailCustomer(customerId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailCustomer = (customerId: string) => {
    setIsLoading(true);
    dispatch(getCustomerDetail(customerId))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;
        const formattedData: IFormCustomer = {
          firstName: data.firstName || EMPTY_STRING,
          lastName: data.lastName || EMPTY_STRING,
          tag: data.tag || EMPTY_STRING,
          avatarImage: data.avatarImage ? { url: data.avatarImage } : {},
          phoneNumber: data.phoneNumber ? formatPhoneNumber(data.phoneNumber) : EMPTY_STRING,
          email: data.email || EMPTY_STRING,
          addresses: data.addresses
            ? data.addresses.map((address) => {
                return {
                  address: address.address || EMPTY_STRING,
                  city: address.city || EMPTY_STRING,
                  address2: address.address2 || EMPTY_STRING,
                  country: address.country || EMPTY_STRING,
                  state: address.state || EMPTY_STRING,
                  zipCode: address.zipCode || EMPTY_STRING,
                  isBillingAddress: address.isBillingAddress,
                  longitude: address.longitude || 0,
                  latitude: address.latitude || 0,
                };
              })
            : [],
          customerContacts: data.customerContacts
            ? data.customerContacts.map((customerContact) => {
                return {
                  email: customerContact.email,
                  name: customerContact.name,
                  phoneNumber: customerContact.phoneNumber
                    ? formatPhoneNumber(customerContact.phoneNumber)
                    : EMPTY_STRING,
                };
              })
            : [],
        };

        reset(formattedData);
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleCancelFormCustomer = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset(defaultValuesForm);
  };

  const handleAddCustomer = async (data: IFormCustomer) => {
    setIsLoading(true);

    const newData: IFormCustomer = {
      ...data,
      phoneNumber: removeNonDigits(data?.phoneNumber),
      customerContacts: data?.customerContacts?.map((contact) => ({
        ...contact,
        phoneNumber: contact.phoneNumber ? removeNonDigits(contact.phoneNumber) : undefined,
      })),
    };

    const bodyFormCustomer: IFormCustomer = removeEmptyObjects(newData) as IFormCustomer;

    const formData = await createFormData(bodyFormCustomer, 'avatarImage');

    dispatch(createCustomer(formData))
      .unwrap()
      .then(() => {
        // dispatch(customerActions.setRefreshCustomerList(true));
        handleCancelFormCustomer();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditCustomer = async (data: IFormCustomer) => {
    if (!customerId) return;

    setIsLoading(true);
    const formatData: IFormCustomer = {
      ...data,
      ...(data.avatarImage && isEmptyObject(data.avatarImage)
        ? { avatarImage: undefined, deleteAvatar: true }
        : { avatarImage: data.avatarImage?.file ? data.avatarImage : undefined }),
      customerContacts: data?.customerContacts
        ?.map((contact) => ({
          ...contact,
          phoneNumber: contact.phoneNumber ? removeNonDigits(contact.phoneNumber) : undefined,
        }))
        .filter((c) => c.name?.trim() || c.email?.trim() || c.phoneNumber?.trim()),
      phoneNumber: data?.phoneNumber && removeNonDigits(data?.phoneNumber),
    };
    const formData = await createFormData(formatData, 'avatarImage');

    const payload: IPayloadEditCustomer = {
      customerId: customerId,
      body: formData,
    };

    dispatch(editCustomer(payload))
      .unwrap()
      .then(() => {
        handleCancelFormCustomer();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmitFormCustomer = (data: IFormCustomer) => {
    if (customerId) {
      handleEditCustomer(data);
    } else {
      handleAddCustomer(data);
    }
  };

  const handleAddItemAdditionalContact = () => {
    append(defaultValuesAdditionalContact);
  };

  const handleAddItemAddress = () => {
    const newAddress = {
      ...defaultValuesAddress,
      isBillingAddress: addresses?.length === DEFAULT_NUMBER_ZERO,
    };

    appendAddress(newAddress);
  };

  const handleRemoveFieldAdditionalContact = (index: number) => {
    remove(index);
  };

  const handleRemoveFieldAddress = (index: number) => {
    removeAddress(index);

    const isRemovingBilling = addresses[index]?.isBillingAddress;

    if (isRemovingBilling && addresses?.length > DEFAULT_NUMBER_ONE) {
      setValue(`addresses.${DEFAULT_NUMBER_ZERO}.isBillingAddress`, true, { shouldValidate: true });
    }
  };

  const handleGetListCustomerQuickBooks = (params: IQueryParamBase) => {
    setIsLoadingCustomerQB(true);

    dispatch(getListCustomerQuickBooks(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses } = res?.data;

        const dataFormatted: (IBaseOption & ICustomerQuickBooks)[] = responses?.map((item) => ({
          ...item,
          label: item?.name,
          value: item?.id,
        }));
        setCustomerQBOptions(dataFormatted);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingCustomerQB(false);
      });
  };

  const handleCustomerQBSearchChange = (searchKey: string) => {
    setCustomerQBSearchKey(searchKey);
  };

  const handleCustomerQBChange = (customerQB: ICustomerQuickBooks) => {
    if (!customerQB) return;

    const { firstName: customerQBFirstName, lastName: customerQBlastName } = splitFullName(customerQB?.name);

    setValue('firstName', customerQBFirstName || EMPTY_STRING, { shouldValidate: true });
    setValue('lastName', customerQBlastName || EMPTY_STRING, { shouldValidate: true });
    setValue('email', customerQB?.email || EMPTY_STRING, { shouldValidate: true });
    setValue('phoneNumber', customerQB?.phone || EMPTY_STRING, { shouldValidate: true });
    setValue('syncToken', customerQB?.syncToken || EMPTY_STRING, { shouldValidate: true });
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formCustomerPage'
      title={customerId ? t('form_customer_title_edit') : t('form_customer_title_add')}
      onBackToPreviousPage={handleCancelFormCustomer}
    >
      <>
        <FormProvider
          {...({
            control,
            watch,
            setValue,
            formState: { errors },
          } as UseFormReturn<IFormCustomer>)}
        >
          <Stack
            component={'form'}
            spacing={'20px'}
            onSubmit={handleSubmit(handleSubmitFormCustomer)}
            className={cx('form')}
          >
            <Box component={'div'} className={cx('formBody')}>
              <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
                {!customerId && (
                  <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
                    <Grid2 size={6}>
                      <Controller
                        name='typeLinkQuickBook'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseSelect
                            label={t('field_link_to_quick_books_label')}
                            required
                            value={value || EMPTY_STRING}
                            onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                            options={typeLinkQuickBooksOptions}
                            errorMessage={errors.typeLinkQuickBook?.message}
                          />
                        )}
                      />
                    </Grid2>

                    {typeLinkQuickBooks === TypeLinkQuickBooksEnum.ADD_EXISTING && (
                      <Grid2 size={6}>
                        <Controller
                          name='quickBookCustomerId'
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <BaseSelect
                              label={t('field_choose_quick_books_customer_label')}
                              required
                              value={value || EMPTY_STRING}
                              onChange={(option: IBaseOption & ICustomerQuickBooks) => {
                                onChange(option?.value);
                                handleCustomerQBChange(option);
                              }}
                              options={customerQBOptions || []}
                              isLoading={isLoadingCustomerQB}
                              onSearchChange={handleCustomerQBSearchChange}
                              errorMessage={errors.quickBookCustomerId?.message}
                            />
                          )}
                        />
                      </Grid2>
                    )}
                  </Grid2>
                )}

                {(customerId ||
                  typeLinkQuickBooks === TypeLinkQuickBooksEnum.NO ||
                  typeLinkQuickBooks === TypeLinkQuickBooksEnum.ADD_NEW ||
                  (typeLinkQuickBooks === TypeLinkQuickBooksEnum.ADD_EXISTING && quickBooksCustomerId)) && (
                  <>
                    <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
                      <Grid2 size={12}>
                        <Controller
                          name='avatarImage'
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <FileUpload
                              accept={FILE_UPLOAD_DEFAULT_ACCEPT_IMAGE}
                              label={t('group_avatar_title')}
                              {...(customerId && !value?.file && { uploadedFiles: value?.url })}
                              onChange={(files: IFileUpload[]) => onChange(files?.[DEFAULT_NUMBER_ZERO])}
                              errorMessage={errors.avatarImage?.message}
                            />
                          )}
                        />
                      </Grid2>

                      {/* <Grid2 size={6}>
                    <Controller
                      name='contacts'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseSelect
                          label={t('field_contacts_label')}
                          value={value}
                          onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                          options={
                            contactList?.map((contact) => ({
                              label: getFullName({ ...contact }),
                              value: contact?.id,
                            })) || []
                          }
                          isLoading={isLoadingContacts}
                          onSearchChange={handleSearchContacts}
                          errorMessage={errors.contacts?.message}
                        />
                      )}
                    />
                  </Grid2> */}

                      <Grid2 size={6}>
                        <Controller
                          name='firstName'
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <BaseInput
                              required
                              label={t('field_first_name_label')}
                              value={value}
                              onChange={onChange}
                              errorMessage={errors?.firstName?.message}
                            />
                          )}
                        />
                      </Grid2>

                      <Grid2 size={6}>
                        <Controller
                          name='lastName'
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <BaseInput
                              required
                              label={t('field_last_name_label')}
                              value={value}
                              onChange={onChange}
                              errorMessage={errors?.lastName?.message}
                            />
                          )}
                        />
                      </Grid2>

                      <Grid2 size={6}>
                        <Controller
                          name='email'
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <BaseInput
                              label={t('field_email_label')}
                              value={value}
                              onChange={onChange}
                              errorMessage={errors?.email?.message}
                            />
                          )}
                        />
                      </Grid2>

                      <Grid2 size={6}>
                        <Controller
                          name='phoneNumber'
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <BaseInputPhone
                              label={t('field_phone_number_label')}
                              value={value}
                              onChange={onChange}
                              errorMessage={errors?.phoneNumber?.message}
                            />
                          )}
                        />
                      </Grid2>

                      <Grid2 size={6}>
                        <Controller
                          name='tag'
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <BaseSelect
                              label={t('field_tags_label')}
                              required
                              value={value}
                              onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                              options={mockDataCustomerTags}
                              errorMessage={errors.tag?.message}
                            />
                          )}
                        />
                      </Grid2>
                    </Grid2>

                    <Grid2 container size={12} rowSpacing={'12px'}>
                      <Grid2 size={12}>
                        <Typography color='primary' className={cx('groupTitle')}>
                          {t('group_additional_contact_title')}
                        </Typography>
                      </Grid2>

                      <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                        {fields?.map((item, index) => (
                          <CustomerAdditionalContact
                            index={index}
                            key={item.id}
                            onRemoveItem={handleRemoveFieldAdditionalContact}
                          />
                        ))}

                        <Grid2 size={12}>
                          <Stack
                            direction={'row'}
                            alignItems={'center'}
                            spacing={'4px'}
                            className={cx('addFieldGroup')}
                          >
                            <img
                              src={icons.commonIconAddField}
                              alt={t('altImg', { ns: 'common/base' })}
                              className={cx('addFieldIcon')}
                            />

                            <Typography
                              color='primary'
                              onClick={handleAddItemAdditionalContact}
                              className={cx('addField')}
                            >
                              {t('common_action_add_field', { ns: 'common/base' })}
                            </Typography>
                          </Stack>
                        </Grid2>
                      </Grid2>
                    </Grid2>

                    <Grid2 container size={12} rowSpacing={'12px'}>
                      <Grid2 size={12}>
                        <Typography color='primary' className={cx('groupTitle')}>
                          {t('group_address_title')}
                        </Typography>
                      </Grid2>

                      <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                        {fieldsAddress?.map((item, index) => (
                          <CustomerAddress
                            index={index}
                            key={item.id}
                            onRemoveItem={handleRemoveFieldAddress}
                          />
                        ))}

                        <Grid2 size={12}>
                          <Stack
                            direction={'row'}
                            alignItems={'center'}
                            spacing={'4px'}
                            className={cx('addFieldGroup')}
                          >
                            <img
                              src={icons.commonIconAddField}
                              alt={t('altImg', { ns: 'common/base' })}
                              className={cx('addFieldIcon')}
                            />

                            <Typography
                              color='primary'
                              onClick={handleAddItemAddress}
                              className={cx('addField')}
                            >
                              {t('common_action_add_field', { ns: 'common/base' })}
                            </Typography>
                          </Stack>
                        </Grid2>
                      </Grid2>
                    </Grid2>

                    {/* <Grid2 container size={12} rowSpacing={'12px'}>
                  <Grid2 size={12}>
                    <Typography color='primary' className={cx('groupTitle')}>
                      {t('group_billing_address_title')}
                    </Typography>
                  </Grid2>

                  <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
                    <Grid2 size={4}>
                      <Controller
                        name='billingAddress'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseInputAddress
                            label={t('field_address_label')}
                            value={value}
                            onChange={onChange}
                            onSelectAddress={handleSelectBillingAddress}
                            errorMessage={errors.billingAddress?.message}
                          />
                        )}
                      />
                    </Grid2>

                    <Grid2 size={4}>
                      <Controller
                        name='billingAddress2'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseInputAddress
                            label={t('field_address_second_label')}
                            value={value}
                            onChange={onChange}
                            errorMessage={errors.billingAddress2?.message}
                          />
                        )}
                      />
                    </Grid2>

                    <Grid2 size={4}>
                      <Controller
                        name='billingCity'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseInput
                            label={t('field_city_label')}
                            value={value}
                            onChange={onChange}
                            errorMessage={errors?.billingCity?.message}
                          />
                        )}
                      />
                    </Grid2>

                    <Grid2 size={4}>
                      <Controller
                        name='billingState'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseInput
                            label={t('field_state_label')}
                            value={value}
                            onChange={onChange}
                            errorMessage={errors?.billingState?.message}
                          />
                        )}
                      />
                    </Grid2>

                    <Grid2 size={4}>
                      <Controller
                        name='billingZipCode'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseInput
                            label={t('field_zip_code_label')}
                            value={value}
                            onChange={onChange}
                            errorMessage={errors?.billingZipCode?.message}
                          />
                        )}
                      />
                    </Grid2>

                    <Grid2 size={4}>
                      <Controller
                        name='billingCountry'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseInput
                            label={t('field_country_label')}
                            value={value}
                            onChange={onChange}
                            errorMessage={errors?.billingCountry?.message}
                          />
                        )}
                      />
                    </Grid2>
                  </Grid2>
                </Grid2> */}
                  </>
                )}
              </Grid2>
            </Box>

            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'flex-end'}
              component={'div'}
              spacing={'12px'}
              className={cx('formFooter')}
            >
              <Button variant='outlined' onClick={handleCancelFormCustomer}>
                {t('common_action_cancel', { ns: 'common/base' })}
              </Button>

              <Button variant='contained' type='submit' disabled={customerId ? !isDirty : false}>
                {t('common_action_save', { ns: 'common/base' })}
              </Button>
            </Stack>
          </Stack>
        </FormProvider>

        {isLoading && <Loading />}

        <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
      </>
    </ChildrenPage>
  );
};

export default FormCustomer;
